import React, { JSX, useContext, useEffect, useState } from "react";
import { BenchmarkingService } from "@app/overviews/network-benchmarking-dashboard";
import { BenchmarkingFiltersContext, BenchmarkingServiceAreaFilters, NetworkFlyoutCountiesTable, NetworkFlyoutPlansTable } from "@app/overviews/network-benchmarking-dashboard/filters";
import { MedicareFiltersContext, MedicareNetwork } from "@app/overviews/network-benchmarking-dashboard/medicare/filters";
import { Accordion, AccordionContainer, LoadingSpinner } from "@app/shared";
import { printNumberAsString } from "@app/shared/waffles";
import { Roles, UserContext } from "@app/user";
import { Link } from "react-router-dom";

interface MedicareNetworkFlyoutProps {
  contract: MedicareNetwork;
}

const MedicareNetworkFlyout = (props: MedicareNetworkFlyoutProps): JSX.Element => {
  const [contract, setContract] = useState<MedicareNetwork>(null);
  const [isYourNetwork, setIsYourNetwork] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);
  const benchmarkingMedicareFiltersContext = useContext(MedicareFiltersContext);
  const userContext = useContext(UserContext);

  const benchmarkingService = new BenchmarkingService();

  useEffect(() => {
    if (!!props.contract?.contractId) {
      const yourNetworks: MedicareNetwork[] = benchmarkingMedicareFiltersContext.parentOrganizations.find(x => x.parentOrganizationName === "Your QES Networks")?.hContracts;
      setIsYourNetwork((yourNetworks.map(x => x.contractId).includes(props.contract?.contractId)));
    }
  }, [props.contract?.contractId]);

  useEffect(() => {
    if (!!props.contract?.contractId && !!benchmarkingFiltersContext.displayedPrimaryFilters) {
      getContractDetails();
    }
  }, [props.contract?.contractId, benchmarkingFiltersContext.displayedPrimaryFilters]);

  const getContractDetails = (): void => {
    setLoading(true);

    benchmarkingService.getContractDetails(props.contract.contractId, benchmarkingFiltersContext.displayedPrimaryFilters, props.contract.versionId).then((data) => {
      data.parentOrganization = props.contract.parentOrganization;
      data.disabled = props.contract.disabled;
      data.versionId = props.contract.versionId;
      setContract(data);
      setLoading(false);
    });
  }

  return (
    <React.Fragment>
      <BenchmarkingServiceAreaFilters />

      <div className="row">
        <div className="col-12">
          {loading ? (
            <div className="text-center">
              <LoadingSpinner />
            </div>
          ) : (
            <AccordionContainer>
              <Accordion
                defaultOpen={true}
                title="Payer Details"
                testId="h-contract-flyout__payer-details-accordion"
              >
                <div className="row">
                  <div className="col-md-3">
                    <label className="font-weight-bold">
                      Marketing Name:
                    </label>
                  </div>
                  <div className="col-md-9">
                    {contract?.marketingName}
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-3">
                    <label className="font-weight-bold">
                      Organization Name:
                    </label>
                  </div>
                  <div className="col-md-9">
                    {contract?.organizationName}
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-3">
                    <label className="font-weight-bold mb-0">
                      Parent Organization:
                    </label>
                  </div>
                  <div className="col-md-9">
                    {contract?.parentOrganization}
                  </div>
                </div>
              </Accordion>

              <Accordion
                title={`${!isYourNetwork ? "Active " : ""}Counties`}
                testId="h-contract-flyout__counties-accordion"
              >
                <NetworkFlyoutCountiesTable
                  contract={contract}
                  data={contract?.counties}
                  isYourNetwork={isYourNetwork}
                  loading={loading}
                  onApplyServiceAreaClick={benchmarkingMedicareFiltersContext.onApplyServiceAreaClick}
                />
              </Accordion>

              <Accordion
                title="Plans"
                testId="h-contract-flyout__plans-accordion"
              >
                <NetworkFlyoutPlansTable
                  contractId={contract?.contractId}
                  data={contract?.plans}
                />
              </Accordion>

              {(userContext.roles.includes(Roles.CLIENT_ADMIN) || isYourNetwork) && (
                <Accordion
                  title="Data Details"
                  testId="h-contract-flyout__data-details"
                >
                  <div className="row">
                    <div className="col-md-12">
                      <label className="label-m">
                        QES Data
                      </label>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-3">
                      <label className="font-weight-bold">
                        QES Project Id:
                      </label>
                    </div>
                    <div className="col-md-9">
                      {!!props.contract?.qesProjectId ?
                        <Link to={`/projects/${props.contract.qesProjectId}`} target="_blank">{props.contract.qesProjectId}</Link> :
                        "---"
                      }
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-3">
                      <label className="font-weight-bold">
                        QES Active County Count:
                      </label>
                    </div>
                    <div className="col-md-9">
                      {!!props.contract?.qesActiveCountyCount ? printNumberAsString(props.contract?.qesActiveCountyCount, "---") : "---"}
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-3">
                      <label className="font-weight-bold">
                        CMS Active County Count:
                      </label>
                    </div>
                    <div className="col-md-9">
                      {!!props.contract?.cmsCountyCount ? printNumberAsString(props.contract?.cmsCountyCount, "---") : "---"}
                    </div>
                  </div>

                  {userContext.roles.includes(Roles.CLIENT_ADMIN) && (
                    <React.Fragment>
                      <div className="row">
                        <div className="col-md-12">
                          <label className="label-m">
                            Publicly Available Data
                          </label>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-3">
                          <label className="font-weight-bold">
                            Publicly Available Data:
                          </label>
                        </div>
                        <div className="col-md-9">
                          {props.contract?.isDefactoAvailable ? "Yes" : "No"}
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-3">
                          <label className="font-weight-bold">
                            Facility Data Present:
                          </label>
                        </div>
                        <div className="col-md-9">
                          {props.contract?.isFacilityDataPresent ? "Yes" : "No"}
                        </div>
                      </div>
                    </React.Fragment>
                  )}
                </Accordion>
              )}
            </AccordionContainer>
          )}
        </div>
      </div>
    </React.Fragment>
  );
}

export default MedicareNetworkFlyout;
